// Shareable links for the Rollout Readout, in `#rollout=…` (see src/lib/share.js).

import { bool, defineShare, num, obj, oneOf, str } from "../lib/share.js";
import { MODES } from "./model.js";

const BIG = 1e12;
const group = (raw) => {
  const o = obj(raw);
  const events = num(o.events, NaN, 0, BIG);
  return { events: Number.isFinite(events) ? Math.round(events) : NaN, exposure: num(o.exposure, NaN, 0, BIG) };
};

export const rolloutShare = defineShare({
  key: "rollout",
  version: 1,
  docs: {
    summary: "Whether a launch changed an event rate without an A/B platform: before against after with an allowance for drift, or accounts that got the feature against accounts that did not, picked at random.",
    fields: {
      mode: "before-after (a change after the launch, not an effect) or randomized (accounts picked at random; an effect).",
      eventLabel: "What is counted, e.g. activations; at most 60 characters.",
      exposureLabel: "What the count is out of, e.g. signups or account-weeks; at most 60 characters.",
      history: "Before/after only: pre-launch periods, one per line as events/exposure (e.g. 102/410), at most 8,000 characters. Two or more lines measure the rate's usual swing and replace base with their total.",
      afterPeriods: "Before/after with history: how many periods of the same length the after window covers (1 to 520).",
      base: "Before the launch, or without the feature: { events (whole number), exposure (above 0) }. Ignored when history has two or more periods.",
      test: "After the launch, or with the feature: { events (whole number), exposure (above 0) }.",
      driftPct: "Before/after only: ± percent, as a 90% range, for changes the history cannot show (a campaign, a price change), added to the measured swing; without history it is the whole allowance (0 to 200).",
      higherIsBetter: "True when more events is good (activations), false when fewer is good (tickets).",
      targetPct: "Smallest change, in percent, that matters (0 to 1000).",
    },
    caveats: [
      "A before/after shows a change after the launch, not what the launch caused. Without pre-launch history its range rests on a judged allowance, not measured variation.",
      "The measured swing assumes periods vary around a steady level; a trend or season before the launch needs the extra allowance.",
      "Counts are treated as Poisson over their exposure; repeated events from the same user make the range too narrow.",
    ],
  },
  toState: (s) => ({
    mode: s.mode,
    history: s.history,
    afterPeriods: s.afterPeriods,
    eventLabel: s.eventLabel,
    exposureLabel: s.exposureLabel,
    base: { ...s.base },
    test: { ...s.test },
    driftPct: s.driftPct,
    higherIsBetter: s.higherIsBetter,
    targetPct: s.targetPct,
  }),
  fromState: (raw) => ({
    mode: oneOf(raw.mode, MODES, "before-after"),
    history: str(raw.history, "", 8000),
    afterPeriods: Math.round(num(raw.afterPeriods, 1, 1, 520)),
    eventLabel: str(raw.eventLabel, "events", 60),
    exposureLabel: str(raw.exposureLabel, "exposure", 60),
    base: group(raw.base),
    test: group(raw.test),
    driftPct: num(raw.driftPct, 10, 0, 200),
    higherIsBetter: bool(raw.higherIsBetter, true),
    targetPct: num(raw.targetPct, 5, 0, 1000),
  }),
});
